<template>
    <div class="download-pdf">
        <div class="header">
            <div class="logo">
                <img src="/img/icons/pdf-icon.png" alt="MedMatch Logo" />
            </div>
        </div>
        <div class="title">
            <h2>{{ profileTitle }}</h2>
        </div>
        <div v-for="[sectionName, sectionData] in filteredExportSections" :key="sectionName" class="section">
            <h3 class="section-title">{{ formatTitle(sectionName) }}</h3>

            <table v-if="isPlainObject(sectionData)" class="table">
                <tbody>
                    <tr v-for="(value, key) in sectionData" :key="key">
                        <td class="key">{{ formatKey(key) }}</td>
                        <td class="value">{{ value || 'N/A' }}</td>
                    </tr>
                </tbody>
            </table>

            <table v-else-if="Array.isArray(sectionData)" class="table">
                <thead>
                    <tr>
                        <th v-for="(col, index) in arrayHeaders(sectionData)" :key="index">
                            {{ formatKey(col) }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, rowIndex) in sectionData" :key="rowIndex">
                        <td v-for="(col, colIndex) in arrayHeaders(sectionData)" :key="colIndex">
                            {{ row[col] || 'N/A' }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="print-button">
            <common-button label="Download PDF" type="primary" @click="printProfile"></common-button>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            exportData: { type: Object, required: true },
            patientDetails: { type: Object, required: true },
        },

        computed: {
            profileTitle() {
                const { firstName, middleName, lastName } = this.patientDetails

                return (
                    [firstName, middleName, lastName]
                        .filter(Boolean)
                        .map((name) => name.toUpperCase())
                        .join(' ') + ' PROFILE'
                )
            },
            filteredExportSections() {
                return Object.entries(this.exportData).filter(([, sectionData]) => this.hasData(sectionData))
            },
        },
        methods: {
            isPlainObject(value) {
                return value && typeof value === 'object' && !Array.isArray(value)
            },
            hasData(value) {
                if (Array.isArray(value)) return value.length > 0
                if (this.isPlainObject(value)) return Object.keys(value).length > 0
                return false
            },

            arrayHeaders(arr) {
                if (!arr.length) return []
                return Object.keys(arr[0])
            },

            formatKey(key) {
                return key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())
            },

            formatTitle(title) {
                return title.replace(/([A-Z])/g, ' $1').trim()
            },
            printProfile() {
                const content = this.$el.innerHTML
                const title = this.profileTitle.replace(/\s+/g, '_')

                const printWindow = window.open('', '_blank', 'width=900,height=700')

                if (!printWindow) {
                    alert('Please allow popups to print the profile')
                    return
                }

                printWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <meta charset="UTF-8" />
                        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                        <title>${title}</title>

                        <!-- EXACT styles copied from component -->
                        <style>
                            * {
                                box-sizing: border-box;
                            }

                            body {
                                background: white;
                                margin: 0;
                                padding: 20px;
                                font-family: Arial, sans-serif;
                                color: #333;
                            }

                            .download-pdf {
                                background: white;
                                padding: 40px;
                                max-width: 800px;
                                margin: 0 auto;
                            }

                            .header {
                                text-align: center;
                                margin-bottom: 20px;
                            }

                            .logo img {
                                width: 140px;
                            }

                            .title {
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                gap: 10px;
                                margin-bottom: 30px;
                            }

                            .section {
                                margin-bottom: 30px;
                                page-break-inside: avoid;
                            }

                            .section-title {
                                text-align: left;
                                font-size: 18px;
                                margin-bottom: 10px;
                                border-bottom: 2px solid #e5e7eb;
                                padding-bottom: 6px;
                            }

                            .table {
                                width: 100%;
                                border-collapse: collapse;
                                font-size: 13px;
                            }

                            .table th,
                            .table td {
                                border: 1px solid #ddd;
                                padding: 8px 10px;
                                text-align: left;
                            }

                            .table th {
                                background: #f5f7fa;
                                font-weight: 600;
                            }

                            .key {
                                width: 30%;
                                font-weight: 600;
                                background: #fafafa;
                            }

                            .value {
                                width: 70%;
                            }

                            .print-button {
                                display: none !important;
                            }

                            @page {
                                size: A4;
                                margin: 1cm;
                            }

                            @media print {
                                body {
                                    padding: 0;
                                }

                                .download-pdf {
                                    padding: 0;
                                    max-width: 100%;
                                }
                            }
                        </style>
                    </head>
                    <body>
                        ${content}
                    </body>
                    </html>
                `)

                printWindow.document.close()

                printWindow.onload = () => {
                    setTimeout(() => {
                        printWindow.focus()
                        printWindow.print()
                    }, 300)
                }
                this.emitEvent({ name: 'download-pdf' })
            },
        },
    }
</script>
<style lang="css" scoped>
    .download-pdf {
        background: white;
        padding: 40px;
        max-width: 800px;
        margin: 0 auto;

        font-family: Arial, sans-serif;
        color: #333;
    }

    .header {
        text-align: center;
        margin-bottom: 20px;
    }

    .logo img {
        width: 140px;
    }

    .title {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        margin-bottom: 30px;
    }

    .section {
        margin-bottom: 30px;
    }

    .section-title {
        text-align: left;
        font-size: 18px;
        margin-bottom: 10px;
        border-bottom: 2px solid #e5e7eb;
        padding-bottom: 6px;
    }

    .table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
    }

    .table th,
    .table td {
        border: 1px solid #ddd;
        padding: 8px 10px;
        text-align: left;
    }

    .table th {
        background: #f5f7fa;
        font-weight: 600;
    }

    .key {
        width: 30%;
        font-weight: 600;
        background: #fafafa;
    }

    .value {
        width: 70%;
    }

    .print-button {
        margin-top: 40px;
        text-align: center;
    }
    @media print {
        body {
            background: white;
        }

        .download-pdf {
            padding: 0;
            max-width: 100%;
        }

        .print-button {
            display: none;
        }

        .section {
            page-break-inside: avoid;
        }

        .section-title {
            page-break-after: avoid;
        }
    }
</style>
